import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Building2, Check, Mail, Trash2, User, Users } from 'lucide-react';
import Navbar from '../components/Landing_Page/Navbar';
import Footer from '../components/Landing_Page/Footer';
import {
  APP_NAME,
  COMPANY_DEFAULT_ADDRESS,
  COMPANY_DEFAULT_EMAIL,
  COMPANY_DEFAULT_WEBSITE,
  COMPANY_LEGAL_NAME,
} from '../lib/brand';

const EFFECTIVE_DATE = 'July 8, 2026';

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

function Section({ title, children }: SectionProps) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-display text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">{title}</h2>
      <div className="space-y-4 text-sm leading-relaxed text-zinc-600 sm:text-base">{children}</div>
    </section>
  );
}

type AccountType = 'organization' | 'staff' | 'customer';

const ACCOUNT_TYPES: {
  id: AccountType;
  label: string;
  description: string;
  icon: typeof Building2;
}[] = [
  {
    id: 'organization',
    label: 'Organization admin',
    description: 'Delete an entire workspace and all organization data.',
    icon: Building2,
  },
  {
    id: 'staff',
    label: 'Staff user',
    description: 'Remove your personal login from a workspace.',
    icon: Users,
  },
  {
    id: 'customer',
    label: 'Customer portal',
    description: 'Remove your borrower portal login only.',
    icon: User,
  },
];

export default function DeleteAccount() {
  const [accountType, setAccountType] = useState<AccountType>('organization');
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const handleRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const typeLabel =
      accountType === 'organization'
        ? 'Organization / workspace admin'
        : accountType === 'staff'
          ? 'Staff user'
          : 'Customer portal user';

    const body = [
      'Account deletion request',
      '',
      `Account type: ${typeLabel}`,
      `Registered email: ${email.trim()}`,
      orgName.trim() ? `Organization name: ${orgName.trim()}` : '',
      reason.trim() ? `Reason: ${reason.trim()}` : '',
      '',
      'I confirm that I want to delete my account and understand that this action may be permanent.',
    ]
      .filter(Boolean)
      .join('\n');

    const subject = encodeURIComponent(`${APP_NAME} account deletion request`);
    const mailBody = encodeURIComponent(body);
    window.location.href = `mailto:${COMPANY_DEFAULT_EMAIL}?subject=${subject}&body=${mailBody}`;
  };

  return (
    <div className="min-h-screen bg-zinc-50 font-sans text-zinc-900 antialiased">
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6">
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-rose-700">Account</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              Delete your account
            </h1>
            <p className="mt-3 text-sm text-zinc-500">
              Effective date: <span className="font-medium text-zinc-700">{EFFECTIVE_DATE}</span>
            </p>
            <p className="mt-6 text-sm leading-relaxed text-zinc-600 sm:text-base">
              You can request deletion of your <strong>{APP_NAME}</strong> account and associated personal data at any
              time. This page explains what is deleted, what may be retained, and how to submit a request.
            </p>

            <div className="mt-10 border-t border-zinc-100 pt-10">
              <Section title="1. Who can use this page">
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Organization administrators</strong> — delete an entire workspace, including admin users,
                    staff access, and organization data.
                  </li>
                  <li>
                    <strong>Staff users</strong> — remove your personal login from an organization workspace.
                  </li>
                  <li>
                    <strong>Customer portal users</strong> — remove your borrower portal login created by your finance
                    provider.
                  </li>
                </ul>
                <p>
                  Customer loan and payment records may be retained by your finance organization if required for legal,
                  accounting, or regulatory purposes, even after your portal login is removed.
                </p>
              </Section>

              <Section title="2. What we delete">
                <p>When your deletion request is approved and processed, we delete or deactivate:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>your user account and login credentials;</li>
                  <li>profile information such as name, email address, and phone number;</li>
                  <li>authentication tokens and session data;</li>
                  <li>organization workspace data, if you are the organization owner requesting full workspace deletion;</li>
                  <li>uploaded media linked only to your account or deleted workspace, where applicable.</li>
                </ul>
              </Section>

              <Section title="3. What may be retained">
                <p>Some information may be kept for a limited period when required by law or legitimate business needs:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>billing, invoice, and payment transaction records (typically up to 7 years for tax and audit purposes);</li>
                  <li>security, fraud-prevention, and access logs (typically up to 90 days);</li>
                  <li>backup copies that are automatically purged on a rolling schedule (typically within 30–90 days);</li>
                  <li>anonymized or aggregated usage statistics that no longer identify you.</li>
                </ul>
              </Section>

              <Section title="4. How to request deletion">
                <p>You can request account deletion in either of these ways:</p>
                <ol className="list-decimal space-y-2 pl-5">
                  <li>
                    Use the form on this page to email our support team from your registered email address.
                  </li>
                  <li>
                    Email{' '}
                    <a href={`mailto:${COMPANY_DEFAULT_EMAIL}`} className="font-medium text-emerald-700 hover:underline">
                      {COMPANY_DEFAULT_EMAIL}
                    </a>{' '}
                    directly with the subject line <strong>"Account deletion request"</strong> and include your registered
                    email, organization name (if applicable), and account type.
                  </li>
                </ol>
                <p>
                  We may ask you to verify your identity before processing the request. Deletion requests are usually
                  completed within <strong>7 business days</strong>, and no later than <strong>30 days</strong> unless a
                  longer period is required by law.
                </p>
              </Section>

              <Section title="5. Before you delete">
                <ul className="list-disc space-y-2 pl-5">
                  <li>Export any reports or records you need before requesting deletion.</li>
                  <li>Cancel active subscriptions to avoid future billing.</li>
                  <li>Ensure no pending payments, holds, or compliance reviews are open on your workspace.</li>
                  <li>Account deletion is permanent and cannot be undone.</li>
                </ul>
              </Section>

              <div className="mb-10 overflow-hidden rounded-2xl border border-rose-200/80 bg-gradient-to-b from-rose-50 to-white shadow-sm">
                <div className="border-b border-rose-100 bg-rose-50/80 px-4 py-4 sm:px-6 sm:py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
                      <Trash2 className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-display text-lg font-semibold text-zinc-900 sm:text-xl">Request account deletion</h2>
                      <p className="mt-0.5 text-xs leading-relaxed text-zinc-600 sm:text-sm">
                        Choose your account type and send a deletion request to our support team.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleRequest} className="space-y-5 px-4 py-5 sm:space-y-6 sm:px-6 sm:py-6">
                  <fieldset>
                    <legend className="mb-3 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Account type
                    </legend>
                    <div className="grid grid-cols-1 gap-2.5 sm:gap-3" role="radiogroup" aria-label="Account type">
                      {ACCOUNT_TYPES.map((opt) => {
                        const on = accountType === opt.id;
                        const Icon = opt.icon;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => setAccountType(opt.id)}
                            className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all sm:px-4 sm:py-3.5 ${
                              on
                                ? 'border-rose-400 bg-white shadow-sm ring-2 ring-rose-500/20'
                                : 'border-zinc-200 bg-zinc-50/80 hover:border-zinc-300 hover:bg-white'
                            }`}
                          >
                            <div
                              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                on ? 'bg-rose-100 text-rose-700' : 'bg-white text-zinc-500'
                              }`}
                            >
                              <Icon className="h-4 w-4" aria-hidden />
                            </div>
                            <div className="min-w-0 flex-1 pr-1">
                              <p className={`text-sm font-semibold leading-tight ${on ? 'text-zinc-900' : 'text-zinc-700'}`}>
                                {opt.label}
                              </p>
                              <p className="mt-1 text-xs leading-relaxed text-zinc-500 sm:text-sm">{opt.description}</p>
                            </div>
                            <div
                              className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                on ? 'border-rose-600 bg-rose-600 text-white' : 'border-zinc-300 bg-white'
                              }`}
                              aria-hidden
                            >
                              {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                    <div className="sm:col-span-2">
                      <label htmlFor="delete-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Registered email
                      </label>
                      <input
                        id="delete-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-sm text-zinc-900 outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20"
                      />
                    </div>

                    {accountType !== 'customer' ? (
                      <div className="sm:col-span-2">
                        <label htmlFor="delete-org" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                          Organization name
                        </label>
                        <input
                          id="delete-org"
                          type="text"
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          placeholder="Your finance business name"
                          className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-sm text-zinc-900 outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20"
                        />
                      </div>
                    ) : null}

                    <div className="sm:col-span-2">
                      <label htmlFor="delete-reason" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Reason <span className="font-normal normal-case text-zinc-400">(optional)</span>
                      </label>
                      <textarea
                        id="delete-reason"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Tell us why you want to delete your account"
                        className="w-full resize-none rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-sm text-zinc-900 outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20"
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50/70 px-3.5 py-3 text-sm leading-relaxed text-zinc-600 sm:px-4">
                    <input
                      type="checkbox"
                      required
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                    />
                    <span>
                      I understand that account deletion is permanent and that some billing or legal records may be
                      retained as described above.
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-700 active:bg-rose-800"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    Email deletion request
                  </button>
                </form>
              </div>

              <Section title="6. Contact">
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
                  <p className="font-semibold text-zinc-900">{COMPANY_LEGAL_NAME}</p>
                  <p className="mt-2">{APP_NAME} Support</p>
                  <p className="mt-2">
                    Email:{' '}
                    <a href={`mailto:${COMPANY_DEFAULT_EMAIL}`} className="font-medium text-emerald-700 hover:underline">
                      {COMPANY_DEFAULT_EMAIL}
                    </a>
                  </p>
                  <p className="mt-1">Website: {COMPANY_DEFAULT_WEBSITE}</p>
                  <p className="mt-1">Address: {COMPANY_DEFAULT_ADDRESS}</p>
                </div>
              </Section>
            </div>

            <div className="mt-10 flex flex-wrap gap-3 border-t border-zinc-100 pt-8">
              <NavLink
                to="/privacy-policy"
                className="inline-flex items-center rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-emerald-300 hover:text-emerald-800"
              >
                Privacy Policy
              </NavLink>
              <NavLink
                to="/login"
                className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                Sign in
              </NavLink>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
